// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CustomArticleService } from '@article/service/custom-article.service';
import { UserModel } from '@user/model/user.model';
import { lastValueFrom } from 'rxjs';
import { ArticleNumberMappingVO } from '../../../model/vo/article-number-mapping.vo';
import { ArticleNumberMappingUtils } from '../../../util/article-number-mapping-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticleVO } from '../../../model/vo/article.vo';
import { ArticleDataService } from '../../../service/article-data.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CustomValidators } from '@shared/custom-validators';

/**
 * Component to edit a user-custom article number
 * It is accessible via the user settings
 */
@Component({
  standalone: false,
  selector: 'custom-article-number',
  templateUrl: './edit-custom-article-number.component.html'
})
export class EditCustomArticleNumberComponent implements OnInit {
  @Input() articleNumberMapping: ArticleNumberMappingVO;
  @Input() createdMappings: ArticleNumberMappingVO[];
  @Output() public finish = new EventEmitter<ArticleNumberMappingVO | undefined>();

  private readonly MAX_ARTICLE_NUMBER_LENGTH = 200;

  public form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private bsModalRef: BsModalRef,
    private alertHelper: AlertHelper,
    public articleDataService: ArticleDataService,
    private customArticleService: CustomArticleService,
    private userModel: UserModel,
    ) {
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      'articleNumber': ['', CustomValidators.trimRequired],
      'customNumber': ['', Validators.compose([CustomValidators.trimRequired, CustomValidators.maxLength(this.MAX_ARTICLE_NUMBER_LENGTH)])]
    });
    if (this.articleNumberMapping) {
      this.form.controls.articleNumber.setValue(this.articleNumberMapping.articleNumber);
      this.form.controls.customNumber.setValue(this.articleNumberMapping.customArticleNumber);
    }
  }

  public async accept(): Promise<void> {
    if (!this.form.valid) {
      return;
    }
    if (this.isMappingWithArticleNumberExists()) {
      this.showAlreadyExistsErrorMessage();
      return;
    }
    if (!this.articleNumberMapping) {

      const existingArticle = await lastValueFrom(this.articleDataService.loadArticleByArticleNumber(this.form.value.articleNumber.trim()));

      if(existingArticle) {
        this.articleByNumberLoaded(existingArticle);
        this.closeWithSuccess();
        return;
      }

      const existingCustomArticleId = await this.findMatchingCustomArticleId();
      if(existingCustomArticleId != undefined) {
        this.closeWithSuccess();
        return;
      }

      this.showInvalidNumberErrorMessage()

    } else {
      this.closeWithSuccess();
    }
  }

  private isMappingWithArticleNumberExists(): boolean {
    if (!this.createdMappings) {
      return false;
    }
    for (const mapping of this.createdMappings) {
      if (this.articleNumberMapping && ArticleNumberMappingUtils.isEqualMappings(this.articleNumberMapping, mapping)) {
        continue;
      }
      if (this.form.value.articleNumber.trim() === mapping.articleNumber ||
        this.form.value.customNumber.trim() === mapping.customArticleNumber) {
        return true;
      }
    }
    return false;
  }

  private showAlreadyExistsErrorMessage(): void {
    this.alertHelper.showError(
      'editCustomArticleNumberPopup.error.title',
      'editCustomArticleNumberPopup.error.article_number_exists_message'
    );
  }

  private showInvalidNumberErrorMessage(): void {
    this.alertHelper.showError(
      'editCustomArticleNumberPopup.error.title',
      'editCustomArticleNumberPopup.error.invalid_article_number', {translationParams: [this.form.value.articleNumber]}
  );
  }

  public articleByNumberLoaded(article: ArticleVO): void {
    if (!article) {
      this.showInvalidNumberErrorMessage();
    } else {
      this.closeWithSuccess();
    }
  }

  private async findMatchingCustomArticleId(): Promise<number | undefined> {
    const lib = await lastValueFrom(this.customArticleService.getCustomArticleLibrariesByUserId(this.userModel.user.id));
    return lib.flatMap(l => l.customArticleInfos).find(l => l.articleNumber == this.form.value.articleNumber.trim())?.id;
  }

  private closeWithSuccess(): void {
    if (!this.articleNumberMapping) {
      this.articleNumberMapping = new ArticleNumberMappingVO();
    }
    this.articleNumberMapping.articleNumber = this.form.value.articleNumber.trim();
    this.articleNumberMapping.customArticleNumber = this.form.value.customNumber.trim();
    this.finish.emit(this.articleNumberMapping);
    this.bsModalRef.hide();
  }

  public close(): void {
    this.finish.emit();
    this.bsModalRef.hide();
  }

  public clearValidationAndClose(): void {
    if (!this.form.controls.articleNumber.touched) {
      this.form.controls.articleNumber.clearValidators();
      this.form.controls.articleNumber.updateValueAndValidity();
    }
    this.close();
  }
}
